/**
 * The term registry, served by the backend (`/meta/config` → `terms`).
 *
 * Every user-facing term has ONE definition, written once in
 * `boond_contracts/terms.py` and rendered with the live thresholds server-side.
 * The SPA never restates a definition: hover hints on KPI cards and map legends
 * read them from here, and the assistant reads the same text — so a label, its
 * tooltip and the assistant's explanation cannot drift apart.
 */

export interface Term {
  /** Language code → definition text (already rendered with thresholds). */
  definition: Record<string, string>;
  window: string;
  owner: string;
  /** Glossary section (`boond_contracts.terms.GROUPS`). */
  group: string;
  /** i18n key of the label the pages show for this term — the glossary renders
   * `t(label_key)`, so a term is never named twice. */
  label_key: string;
  /** Ids of closely related terms. */
  see_also: string[];
}

let current: Record<string, Term> = {};

/** Called once by the config bootstrap with the server's registry. */
export function hydrateTerms(next: Record<string, Term> | undefined): void {
  if (next) current = next;
}

/** A term's definition in `lang` (falling back to English), or undefined
 * before the config has loaded or for an unknown id. */
export function termDefinition(id: string, lang = "en"): string | undefined {
  const term = current[id];
  if (!term) return undefined;
  return term.definition[lang.slice(0, 2)] ?? term.definition.en;
}

/** Every term in the registry's own order (the server writes them grouped and
 * curated), each with its id. Empty before the config has loaded. */
export function allTerms(): Array<Term & { id: string }> {
  return Object.entries(current).map(([id, term]) => ({ id, ...term }));
}
